// src/pages/CaseStudyPage.jsx
import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Github, ExternalLink } from 'lucide-react';
import { caseStudies } from '../models/caseStudiesData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// App Store SVG icon
function AppStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

// Play Store SVG icon
function PlayStoreIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14 8.5c.6.36.6 1.24 0 1.6l-14 8.5c-.66.5-1.6.03-1.6-.8zm2-13.7v11.4L17 12 5 6.8z"/>
    </svg>
  );
}

export default function CaseStudyPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const studyIndex = caseStudies.findIndex((s) => s.slug === slug);
  const study = studyIndex !== -1 ? caseStudies[studyIndex] : null;

  const prevStudy = studyIndex > 0 ? caseStudies[studyIndex - 1] : null;
  const nextStudy = studyIndex < caseStudies.length - 1 ? caseStudies[studyIndex + 1] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!study) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#12121a] flex flex-col justify-between">
        <Navbar />
        <div className="container text-center py-32">
          <h2 className="font-heading text-3xl text-primary mb-4">Case Study Not Found</h2>
          <p className="text-gray-500 mb-6">
            The case study you are looking for does not exist or has been removed.
          </p>
          <Link to="/#case-studies" className="btn btn-primary rounded">
            <ArrowLeft size={16} className="mr-2 inline" /> Back to Case Studies
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#12121a] text-[#393e46] dark:text-[#e1dfed] font-body transition-colors duration-300">
      <Navbar />

      {/* Meyawo Header Mini */}
      <header className="header-mini">
        <h1 className="header-title">{study.title}</h1>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-divider">·</li>
            <li className="breadcrumb-item">
              <Link to="/#case-studies">Case Studies</Link>
            </li>
            <li className="breadcrumb-divider">·</li>
            <li className="breadcrumb-item active" aria-current="page">
              {study.title}
            </li>
          </ol>
        </nav>
      </header>

      {/* Main Content Area */}
      <div className="container py-10 md:py-14">
        {/* Top Back Action Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button
            onClick={() => navigate('/#case-studies')}
            className="btn btn-outline-primary btn-sm rounded shadow-sm inline-flex items-center"
          >
            <ArrowLeft size={15} className="mr-2" /> Back to Case Studies
          </button>

          <div className="flex items-center gap-2">
            <span className="bg-purple-100 dark:bg-[#232334] text-primary dark:text-[#a59cca] font-semibold text-xs px-3 py-1 rounded-full border border-purple-200 dark:border-[#2f2f45]">
              Category: {study.category}
            </span>
          </div>
        </div>

        {/* ─── TWO-COLUMN DETAILS & OVERVIEW ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Column (2 spans) */}
          <div className="lg:col-span-2 space-y-10">
            {study.sections.map((section, idx) => (
              <div key={idx} className="space-y-4">
                <h3 className="font-heading text-2xl text-primary font-bold mb-3">
                  {section.title}
                </h3>

                {section.paragraphs && section.paragraphs.map((para, pi) => (
                  <p key={pi} className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
                    {para}
                  </p>
                ))}

                {/* Single Image / Diagram */}
                {section.image && (
                  <div className="my-6 bg-gray-50 dark:bg-[#171722] p-4 rounded border border-gray-200 dark:border-[#272738] shadow-sm">
                    <img
                      src={section.image}
                      alt={section.imageCaption || section.title}
                      className="w-full max-h-[460px] object-contain rounded"
                    />
                    {section.imageCaption && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3">
                        {section.imageCaption}
                      </p>
                    )}
                  </div>
                )}

                {/* Technical Foundation Note */}
                {section.techFoundation && (
                  <div className="bg-purple-50/60 dark:bg-[#1a1a28] border-l-4 border-primary p-4 rounded-r text-sm text-gray-700 dark:text-gray-300">
                    <strong className="text-primary mr-1">Technical foundation:</strong>
                    {section.techFoundation}
                  </div>
                )}

                {/* Multiple Screens Grid */}
                {section.screens && section.screens.length > 0 && (
                  <div className={`grid grid-cols-1 ${section.screens.length === 2 ? 'md:grid-cols-2' : section.screens.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-1'} gap-4 my-6`}>
                    {section.screens.map((screen, si) => (
                      <div
                        key={si}
                        className="bg-gray-50 dark:bg-[#171722] p-3 rounded border border-gray-200 dark:border-[#272738] flex flex-col justify-between"
                      >
                        <img
                          src={screen.image}
                          alt={screen.caption}
                          className="w-full max-h-[420px] object-contain rounded mb-3"
                        />
                        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                          {screen.caption}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {section.note && (
                  <p className="text-xs text-gray-500 italic">
                    {section.note}
                  </p>
                )}

                {/* Data Table */}
                {section.table && (
                  <div className="my-6 bg-white dark:bg-[#171722] rounded border border-gray-200 dark:border-[#272738] overflow-x-auto shadow-sm">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 dark:bg-[#1c1c28] text-xs uppercase font-semibold text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-[#272738]">
                        <tr>
                          {section.table.headers.map((h, hi) => (
                            <th key={hi} className="px-4 py-3">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-[#272738]">
                        {section.table.rows.map((row, ri) => (
                          <tr key={ri} className="hover:bg-gray-50/50 dark:hover:bg-[#1a1a26]">
                            {row.map((cell, ci) => (
                              <td
                                key={ci}
                                className={`px-4 py-3 text-gray-700 dark:text-gray-300 ${ci === 0 ? 'font-semibold' : ''}`}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Technical Challenges (matching ProjectDetailsPage style) */}
                {section.challenges && section.challenges.map((c, ci) => (
                  <div
                    key={ci}
                    className="bg-purple-50/60 dark:bg-[#1a1a28] border-l-4 border-primary p-6 rounded-r shadow-sm my-4"
                  >
                    <h4 className="font-heading text-lg text-primary font-bold mb-2">
                      {c.title}
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                      {c.text}
                    </p>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Sidebar Column (1 span) - matches ProjectDetailsPage */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-[#171722] border border-gray-200 dark:border-[#272738] rounded p-6 shadow-sm">
              <h4 className="font-heading text-lg text-primary font-bold mb-4 pb-2 border-b border-gray-100 dark:border-[#272738]">
                Project Metadata
              </h4>

              <div className="space-y-4 text-sm">
                <div>
                  <span className="block text-xs uppercase font-semibold text-gray-400">
                    Category
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {study.category}
                  </span>
                </div>

                <div>
                  <span className="block text-xs uppercase font-semibold text-gray-400">
                    Author & Role
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {study.developer} — {study.role}
                  </span>
                </div>

                <div>
                  <span className="block text-xs uppercase font-semibold text-gray-400">
                    Target Platforms
                  </span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {study.platforms}
                  </span>
                </div>

                <div>
                  <span className="block text-xs uppercase font-semibold text-gray-400 mb-2">
                    Technologies Used
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {study.tech.map((t) => (
                      <span
                        key={t}
                        className="bg-gray-100 dark:bg-[#232334] text-gray-700 dark:text-gray-300 text-xs px-2.5 py-1 rounded border border-gray-200 dark:border-[#2e2e42]"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-[#272738] space-y-3">
                <span className="block text-xs uppercase font-semibold text-gray-400 mb-2">
                  Project Links
                </span>

                {study.links.pubdev && (
                  <a
                    href={study.links.pubdev}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <ExternalLink size={15} />
                    <span>View on pub.dev</span>
                  </a>
                )}

                {study.links.appstore && (
                  <a
                    href={study.links.appstore}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <AppStoreIcon />
                    <span>Download on App Store</span>
                  </a>
                )}

                {study.links.playstore && (
                  <a
                    href={study.links.playstore}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm rounded w-full flex items-center justify-center gap-2"
                  >
                    <PlayStoreIcon />
                    <span>Get on Google Play</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ─── BOTTOM PREV / NEXT NAVIGATION ─── */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-[#272738] flex flex-col sm:flex-row items-center justify-between gap-4">
          {prevStudy ? (
            <Link
              to={`/case-study/${prevStudy.slug}`}
              className="text-left group flex items-center gap-3 p-3 rounded hover:bg-gray-50 dark:hover:bg-[#171722] transition-colors"
            >
              <div className="w-9 h-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all">
                ←
              </div>
              <div>
                <span className="block text-xs text-gray-400">Previous Case Study</span>
                <span className="font-heading text-sm text-primary font-semibold group-hover:underline">
                  {prevStudy.title}
                </span>
              </div>
            </Link>
          ) : (
            <div></div>
          )}

          <Link
            to="/#case-studies"
            className="btn btn-outline-primary btn-sm rounded shadow-sm"
          >
            All Case Studies
          </Link>

          {nextStudy ? (
            <Link
              to={`/case-study/${nextStudy.slug}`}
              className="text-right group flex items-center gap-3 p-3 rounded hover:bg-gray-50 dark:hover:bg-[#171722] transition-colors"
            >
              <div>
                <span className="block text-xs text-gray-400">Next Case Study</span>
                <span className="font-heading text-sm text-primary font-semibold group-hover:underline">
                  {nextStudy.title}
                </span>
              </div>
              <div className="w-9 h-9 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all">
                →
              </div>
            </Link>
          ) : (
            <div></div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
